import React from 'react';
import UnitIcon from '@components/game/objects/UnitIcon';
import EffectIcon from '@components/ui/icons/EffectIcon';
import HoverClickPopup from '@components/ui/general/HoverClickPopup';
import { formatStatWithModifier, getModifierBreakdown } from "@utils/statCalculations";
import { getSourceIcon } from "@utils/statIcons";
import useGameStore from "@store/useGameStore";
import "./StatDisplay.css";
import "./StatPopup.css";

/**
 * Component to display a stat with modifiers and detailed tooltip
 */
export default function StatDisplay({ unit, statName, icon, tooltip }) {
  const statInfo = formatStatWithModifier(unit, statName);
  const breakdown = getModifierBreakdown(unit, statName);
  
  // Build the stat value display with visual enhancements
  const renderStatValue = () => {
    if (!statInfo.hasModifier) {
      return <span className="stat-value">{statInfo.text}</span>;
    }
    
    // Show effective value in color based on modifier sum
    // Green if buffed (positive modifiers), Red if debuffed (negative modifiers)
    const valueClass = statInfo.modifier > 0 ? 'positive' : 
                      statInfo.modifier < 0 ? 'negative' : 
                      'neutral';
    
    return (
      <span className={`stat-value modified ${valueClass}`}>
        {statInfo.effective}
      </span>
    );
  };
  
  // Determine modifier class and icon color based on buff/debuff status
  const getModifierStyles = () => {
    if (!statInfo.hasModifier) {
      return { className: '', strokeColor: 'currentColor' };
    }
    
    if (statInfo.modifier > 0) {
      return { className: 'positive', strokeColor: '#12e05b' }; // var(--color-green-light)
    } else if (statInfo.modifier < 0) {
      return { className: 'negative', strokeColor: '#ef4444' }; // var(--color-red)
    } else {
      return { className: 'neutral', strokeColor: 'currentColor' };
    }
  };
  
  const modifierStyles = getModifierStyles();
  
  // Clone icon with stroke color
  const styledIcon = icon ? React.cloneElement(icon, {
    stroke: modifierStyles.strokeColor,
    className: icon.props.className
  }) : null;
  
  // Trigger element for the popup
  const triggerElement = (
    <span 
      className={`stat-display ${modifierStyles.className}`}
      style={{ cursor: 'pointer' }}
    >
      {styledIcon}
      {renderStatValue()}
    </span>
  );
  
  // Popup content
  const popupContent = (
    <div className="stat-popup">
      {/* Header with icon and title */}
      <div className="stat-popup-header">
        <div className="stat-popup-title-group">
          <span className="stat-popup-icon">{styledIcon}</span>
          <span className="stat-popup-title">
            {statName.charAt(0).toUpperCase() + statName.slice(1)}
          </span>
        </div>
      </div>
      
      {/* Description */}
      <div className="stat-popup-description">
        {tooltip}
      </div>
      
      {/* Modifiers section - only show if stat has modifiers */}
      {statInfo.hasModifier && (
        <>
          <div className="stat-popup-separator" />
          <div className="stat-popup-modifiers">
            {/* Base value */}
            <div className="stat-base-row">
              <span>Base</span>
              <span className="stat-value">{breakdown.base}</span>
            </div>
            
            {/* Modifiers list */}
            {breakdown.modifiers.length > 0 && (
              <>
                <div className="stat-modifier-separator" />
                <div className="stat-modifiers-section">
                  {breakdown.modifiers.map((mod, index) => {
                    const isPositive = mod.value > 0;
                    const sign = isPositive ? '+' : '';
                    const rowClass = isPositive ? 'positive' : 'negative';
                    
                    // Render icon based on source type
                    const renderSourceIcon = () => {
                      // Check if source is a unit with valid ID
                      if (mod.source?.type === 'unit' && mod.source?.id) {
                        // Validate the ID is a number
                        const unitId = parseInt(mod.source.id);
                        if (!isNaN(unitId) && unitId > 0) {
                          // Fetch the unit object using the ID
                          const sourceUnit = useGameStore.getState().getUnitOrAvailableUnitById(unitId);
                          if (sourceUnit) {
                            return (
                              <span className="modifier-icon" style={{ pointerEvents: 'none' }}>
                                <UnitIcon 
                                  unitId={sourceUnit.id}
                                  size="small"
                                />
                              </span>
                            );
                          } else {
                            console.warn('[StatDisplay] Could not find unit for ID:', unitId);
                          }
                        } else {
                          console.warn('[StatDisplay] Invalid unit ID in source:', mod.source);
                        }
                      }
                      return <span className="modifier-icon">{getSourceIcon(mod.source)}</span>;
                    };
                    
                    return (
                      <div key={`mod-${index}`} className={`stat-modifier-row ${rowClass}`}>
                        {renderSourceIcon()}
                        <span className="modifier-name">
                          <EffectIcon 
                            effectId={mod.effectId}
                            variant="with-name"
                            className={`effect-modifier ${rowClass}`}
                          />
                        </span>
                        <span className="modifier-value">{sign}{mod.value}</span>
                        <span className={`modifier-duration ${mod.unlimited ? 'unlimited' : ''}`}>
                          {(() => {
                            if (mod.unlimited) {
                              // Check if this modifier comes from a counter effect
                              if (mod.counter_state) {
                                const current = mod.counter_state.current_turn_triggers || 0;
                                const max = mod.counter_state.max_per_turn || 0;
                                return `(∞ • ${current}/${max})`;
                              }
                              return '(∞)';
                            }
                            return `(${mod.duration}/${mod.max_turns} turns)`;
                          })()}
                        </span>
                      </div>
                    );
                  })}
                </div>
                
                {/* Total/Effective value */}
                <div className="stat-modifier-separator" />
                <div className="stat-total-row">
                  <span>Effective</span>
                  <span className="stat-value-final">{breakdown.effective}</span>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
  
  return (
    <HoverClickPopup
      trigger={triggerElement}
      className="stat-popup-container"
    >
      {popupContent}
    </HoverClickPopup>
  );
}